<script setup lang="ts">
import { type Component, computed, ref } from 'vue';
import { gaiaFontSize, gaiaNeutral } from '@/components/design/themes/tokens';
import type { BreadcrumbProps } from 'naive-ui';
import { IconMore, IconReturn } from '@/components/design/icons';

export interface BreadcrumbItem {
  label: string;
  icon?: Component | null;
  clickable?: boolean;
  onClick?: (() => void) | null;
}

interface Props {
  items: BreadcrumbItem[];
  maxVisibleItems?: number;
  /**
   * 'chips': file-manager style — hoverable chip crumbs with a "›" separator
   * and an emphasized current (last) crumb that doubles as a section heading.
   */
  variant?: 'default' | 'chips';
}

const props = withDefaults(defineProps<Props>(), {
  maxVisibleItems: 4,
  variant: 'default',
});

// --- chrome (tokens for the scoped styles) ---
const fsBody = gaiaFontSize.body;
const fsRow = gaiaFontSize.row;
const gInk = gaiaNeutral.ink;
const gText = gaiaNeutral.text;
const gSubtle = gaiaNeutral.textSubtle;
const gFaint = gaiaNeutral.textFaint;
const gHover = gaiaNeutral.surfaceHover;

const showOverflowPopover = ref(false);

// Chips draw their own pill hover background — turn off Naive's built-in
// item hover surface so the two don't stack (pill inside a rectangle).
const chipsThemeOverrides: NonNullable<BreadcrumbProps['themeOverrides']> = {
  itemColorHover: 'transparent',
  itemColorPressed: 'transparent',
};

const breadcrumbDisplay = computed(() => {
  const total = props.items.length;
  const max = props.maxVisibleItems;

  if (total <= max) {
    return {
      hasOverflow: false,
      visibleItems: props.items.map((item, index) => ({ item, index })),
      hiddenItems: [],
    };
  }

  return {
    hasOverflow: true,
    visibleItems: [],
    hiddenItems: props.items.slice(1, total - 2),
  };
});

function handleNavigate(item: BreadcrumbItem): void {
  if (item.clickable === false) {
    return;
  }

  if (item.onClick) {
    item.onClick();
    showOverflowPopover.value = false;
  }
}
</script>

<template>
  <div class="breadcrumb-wrapper" :class="{ 'breadcrumb-wrapper--chips': variant === 'chips' }">
    <NBreadcrumb
      :separator="variant === 'chips' ? '›' : '/'"
      :theme-overrides="variant === 'chips' ? chipsThemeOverrides : undefined"
    >
      <template v-if="!breadcrumbDisplay.hasOverflow">
        <NBreadcrumbItem v-for="(item, index) in items" :key="index">
          <div
            class="crumb-node"
            :class="{
              'crumb-node--link': item.clickable !== false,
              'crumb-node--current': index === items.length - 1,
            }"
            @click="handleNavigate(item)"
          >
            <NIcon v-if="item.icon" :component="item.icon" :size="16" />
            <span>{{ item.label }}</span>
          </div>
        </NBreadcrumbItem>
      </template>

      <template v-else>
        <NBreadcrumbItem>
          <div
            class="crumb-node"
            :class="{ 'crumb-node--link': items[0].clickable !== false }"
            @click="handleNavigate(items[0])"
          >
            <NIcon v-if="items[0].icon" :component="items[0].icon" :size="16" />
            <span>{{ items[0].label }}</span>
          </div>
        </NBreadcrumbItem>

        <NBreadcrumbItem>
          <NPopover
            v-model:show="showOverflowPopover"
            trigger="click"
            placement="bottom-start"
            :show-arrow="false"
            class="tree-popover"
            style="padding: 8px"
          >
            <template #trigger>
              <div class="crumb-ellipsis-wrapper">
                <NButton text size="tiny" class="crumb-ellipsis">
                  <NIcon :size="20" :component="IconMore" />
                </NButton>
              </div>
            </template>

            <div class="tree-menu">
              <div
                v-for="(item, i) in breadcrumbDisplay.hiddenItems"
                :key="i"
                class="tree-item"
                :class="{ 'tree-item--link': item.clickable !== false }"
                :style="{ paddingLeft: `${i * 12}px` }"
                @click="handleNavigate(item)"
              >
                <div class="tree-connector">
                  <NIcon v-if="i > 0" :component="IconReturn" class="connector-icon" />
                </div>
                <div class="tree-content">
                  <NIcon v-if="item.icon" :component="item.icon" class="item-icon" />
                  <span class="item-label">{{ item.label }}</span>
                </div>
              </div>
            </div>
          </NPopover>
        </NBreadcrumbItem>

        <NBreadcrumbItem v-for="i in [2, 1]" :key="i">
          <div
            class="crumb-node"
            :class="{
              'crumb-node--link': items[items.length - i].clickable !== false,
              'crumb-node--current': i === 1,
            }"
            @click="handleNavigate(items[items.length - i])"
          >
            <NIcon
              v-if="items[items.length - i].icon"
              :component="items[items.length - i].icon || undefined"
              :size="16"
            />
            <span>{{ items[items.length - i].label }}</span>
          </div>
        </NBreadcrumbItem>
      </template>
    </NBreadcrumb>
  </div>
</template>

<style scoped>
/* --- Main Breadcrumb Styling --- */
.breadcrumb-wrapper {
  padding: 8px 0;
  width: 100%;
}

.crumb-node {
  display: inline-flex; /* Changed to inline-flex for better baseline align */
  align-items: center;
  gap: 7px;
  font-size: v-bind(fsRow);
  color: v-bind(gSubtle);
  transition: color 0.2s;
  cursor: default;
  line-height: 1.5;
  vertical-align: middle;
}

.crumb-node--link {
  cursor: pointer;
  color: v-bind(gText);
}

.crumb-node--link:hover {
  color: v-bind(gInk);
}

/* --- Chips variant (file-manager style, per the vault mock: 28px chip,
   8px radius, faint › separators, the current crumb in 600 ink). --- */
.breadcrumb-wrapper--chips .crumb-node {
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  transition:
    background-color 0.2s,
    color 0.2s;
}

.breadcrumb-wrapper--chips .crumb-node--link:hover {
  background-color: v-bind(gHover);
  color: v-bind(gInk);
}

.breadcrumb-wrapper--chips .crumb-node--current {
  font-weight: 600;
  color: v-bind(gInk);
}

/* FIX: Wrapped ellipsis to isolate alignment */
.crumb-ellipsis-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  height: 100%;
}

.crumb-ellipsis {
  display: flex;
  align-items: center;
  color: v-bind(gFaint);

  /* Use Transform instead of Top/Position.
       This moves the pixels visually WITHOUT changing layout flow.
       Adjust '2px' to '3px' or '1px' as needed.
    */
  transform: translateY(2px);
}

.crumb-ellipsis:hover {
  color: v-bind(gInk);
  background: transparent;
}

/* --- Tree Structure Popover Styling --- */
.tree-menu {
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: 300px;
}

.tree-item {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: default;
  position: relative;
  transition: background-color 0.2s;
  color: v-bind(gSubtle);
}

.tree-item--link {
  cursor: pointer;
  color: v-bind(gText);
}

.tree-item--link:hover {
  background-color: v-bind(gHover);
  color: v-bind(gInk);
}

.tree-connector {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: v-bind(gFaint);
  opacity: 0.5;
}

.connector-icon {
  font-size: v-bind(fsBody);
}

.tree-content {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.item-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: v-bind(fsRow);
}
</style>
